import { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { StudentSidebar } from '@/components/student/student-sidebar'
import { StudentHeader } from '@/components/student/student-header'
import { StudentBottomNav } from '@/components/student/student-bottom-nav'
import { DailyPlanView } from '@/components/student/daily-plan-view'
import { DisciplineTrackerView } from '@/components/student/discipline-tracker-view'
import { StudyMaterialView } from '@/components/student/study-material-view'
import { StudentQuestionBankView } from '@/components/student/student-question-bank-view'
import { ChatView } from '@/components/student/chat-view'
import { TestHistoryView } from '@/components/student/test-history-view'
import { PerformanceAnalyticsView } from '@/components/student/performance-analytics-view'
import { AnnouncementsView } from '@/components/student/announcements-view'
import { LeaderboardView } from '@/components/student/leaderboard-view'
import { ProfileView } from '@/components/student/profile-view'
import { PaymentsView } from '@/components/student/payments-view'
import { SupportView } from '@/components/student/support-view'
import { StudentMobileMenu } from '@/components/student/student-mobile-menu'
import { WelcomeBanner } from '@/components/student/welcome-banner'
import { StatCard } from '@/components/student/stat-card'
import { ActivityFeed } from '@/components/student/activity-feed'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    TrendingUp, Award, Flame, Clock,
    PlayCircle, CheckCircle, Calendar, Bell, ChevronRight, Timer, Menu
} from 'lucide-react'


// ============================================
// DASHBOARD OVERVIEW (Redesigned)
// ============================================
function DashboardOverview({ setActiveTab }: { setActiveTab: (tab: string) => void }) {
    const navigate = useNavigate()

    const recentActivities = [
        { id: '1', type: 'test' as const, title: 'Completed Weekly Mock Test 4', description: 'Score: 610/720', timestamp: '2h ago' },
        { id: '2', type: 'submission' as const, title: 'Daily Plan Submitted', description: '6.5 hours logged', timestamp: '1d ago' },
        { id: '3', type: 'message' as const, title: 'Message from Dr. Sarah', description: 'Great progress on Physics!', timestamp: '2d ago' },
        { id: '4', type: 'achievement' as const, title: '5 Day Streak!', description: 'Keep up the momentum', timestamp: '3d ago' },
    ]

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            {/* Welcome Banner */}
            <WelcomeBanner
                userName="Arun"
                progress={32}
                onContinue={() => setActiveTab('daily-plan')}
            />

            {/* Stats Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                <StatCard
                    icon={TrendingUp}
                    label="Course Progress"
                    value="32%"
                    trend="up"
                    trendValue="+5% this week"
                    color="blue"
                />
                <StatCard
                    icon={Award}
                    label="Avg. Score"
                    value="580"
                    subtext="/720"
                    trend="up"
                    trendValue="+20"
                    color="green"
                />
                <StatCard
                    icon={Flame}
                    label="Study Streak"
                    value="5"
                    subtext="Days"
                    color="amber"
                />
                <StatCard
                    icon={Clock}
                    label="Next Test"
                    value="12h"
                    subtext="left"
                    color="purple"
                />
            </div>

            {/* Two Column Layout */}
            <div className="grid lg:grid-cols-5 gap-4 md:gap-6">
                {/* Today's Snapshot */}
                <div className="lg:col-span-2">
                    <GlassmorphicCard className="p-4 md:p-6 h-full">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base md:text-lg font-bold text-theme-primary">Today's Snapshot</h3>
                            <span className="text-xs text-theme-secondary bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-full">3 pending</span>
                        </div>

                        <div className="space-y-3 mb-4">
                            {[
                                { subject: 'Physics', task: 'Thermodynamics Ch.4', time: '1h', done: false },
                                { subject: 'Chemistry', task: '50 MCQs - Organic', time: '1.5h', done: true },
                                { subject: 'Biology', task: 'Human Physiology Rev.', time: '2h', done: false },
                            ].map((task, i) => (
                                <div key={i} className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${task.done ? 'bg-green-50 dark:bg-green-900/10' : 'bg-slate-50 dark:bg-slate-800/30'}`}>
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${task.done ? 'bg-green-500' : 'bg-slate-200 dark:bg-slate-700'}`}>
                                        {task.done ? <CheckCircle className="h-4 w-4 text-white" /> : <span className="text-xs font-bold text-slate-500">{i + 1}</span>}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-medium truncate ${task.done ? 'text-green-600 line-through' : 'text-theme-primary'}`}>{task.task}</p>
                                        <p className="text-xs text-theme-secondary">{task.subject} • {task.time}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button className="w-full py-2.5 text-sm font-semibold text-primary-blue hover:bg-blue-50 dark:hover:bg-blue-900/10 rounded-xl transition-colors flex items-center justify-center gap-2">
                            View Full Plan
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </GlassmorphicCard>
                </div>

                {/* Recent Activity */}
                <div className="lg:col-span-3">
                    <ActivityFeed activities={recentActivities} maxItems={4} />
                </div>
            </div>

            {/* Quick Actions (Mobile Floating) */}
            <div className="fixed bottom-20 right-4 lg:hidden flex flex-col gap-2 z-30">
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/student/test/instructions/mock-5')}
                    className="w-12 h-12 rounded-full bg-gradient-to-r from-primary-blue to-blue-600 text-white shadow-lg shadow-blue-500/30 flex items-center justify-center"
                >
                    <PlayCircle className="h-5 w-5" />
                </motion.button>
            </div>
        </div>
    )
}

// ============================================
// TESTS VIEW (Redesigned)
// ============================================
function TestsView() {
    const navigate = useNavigate()
    const [activeFilter, setActiveFilter] = useState<'all' | 'pending' | 'completed'>('all')

    const handleStartTest = () => {
        navigate('/student/test/instructions/mock-5', {
            state: {
                testName: 'Weekly Mock Test 5',
                testId: 'mock-5',
                duration: 180,
                totalQuestions: 200
            }
        })
    }

    const handleViewReport = (score: number, total: number) => {
        navigate('/student/test/results', {
            state: {
                candidateName: 'Arun Student',
                rollNumber: 'NEET-2024-001',
                totalQuestions: total,
                answered: Math.floor(score / 4) + 15,
                notAnswered: total - (Math.floor(score / 4) + 15),
                markedForReview: 5,
                questions: [],
                userAnswers: []
            }
        })
    }

    const pastTests = [
        { num: 4, score: 620, date: 'Oct 16, 2024', status: 'completed' },
        { num: 3, score: 610, date: 'Oct 9, 2024', status: 'completed' },
        { num: 2, score: 600, date: 'Oct 2, 2024', status: 'completed' },
        { num: 1, score: 590, date: 'Sep 25, 2024', status: 'completed' },
    ]

    const scheduledTests = [
        { name: 'Weekly Mock Test 6', date: 'Oct 28, 2024', time: '10:00 AM' },
        { name: 'Chapter Test - Organic', date: 'Oct 30, 2024', time: '2:00 PM' },
    ]

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            {/* Header with Filter */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h2 className="text-xl md:text-2xl font-bold text-theme-primary">My Tests</h2>
                <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                    {(['all', 'pending', 'completed'] as const).map((filter) => (
                        <button
                            key={filter}
                            onClick={() => setActiveFilter(filter)}
                            className={`px-3 md:px-4 py-1.5 text-xs md:text-sm font-medium rounded-md capitalize transition-all ${activeFilter === filter
                                ? 'bg-white dark:bg-slate-700 shadow text-primary-blue'
                                : 'text-slate-500 hover:text-slate-700'
                                }`}
                        >
                            {filter}
                        </button>
                    ))}
                </div>
            </div>

            {/* Active Test - Hero Card */}
            {(activeFilter === 'all' || activeFilter === 'pending') && (
                <GlassmorphicCard className="p-4 md:p-6 relative overflow-hidden border-l-4 border-l-green-500">
                    <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-gradient-to-br from-green-500/20 to-blue-500/10 blur-2xl" />

                    <div className="relative z-10">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold animate-pulse">● LIVE</span>
                                    <span className="text-xs text-theme-secondary">Expires in 12 hours</span>
                                </div>
                                <h3 className="text-xl md:text-2xl font-bold text-theme-primary mb-1">Weekly Mock Test 5</h3>
                                <p className="text-sm text-theme-secondary">Full Syllabus • Physics, Chemistry, Biology • 200 Questions</p>
                            </div>

                            <div className="flex items-center gap-4">
                                {/* Countdown Timer Visual */}
                                <div className="hidden sm:flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                    <Timer className="h-5 w-5 text-green-500" />
                                    <div>
                                        <p className="text-xs text-theme-secondary">Duration</p>
                                        <p className="font-bold text-theme-primary">3 Hours</p>
                                    </div>
                                </div>

                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={handleStartTest}
                                    className="px-6 md:px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl shadow-lg shadow-green-500/30 flex items-center gap-2"
                                >
                                    <PlayCircle className="h-5 w-5" />
                                    <span className="hidden sm:inline">Start Test</span>
                                    <span className="sm:hidden">Start</span>
                                </motion.button>
                            </div>
                        </div>
                    </div>
                </GlassmorphicCard>
            )}

            {/* Scheduled Tests */}
            {(activeFilter === 'all' || activeFilter === 'pending') && scheduledTests.length > 0 && (
                <div>
                    <h3 className="text-base md:text-lg font-bold text-theme-primary mb-3">Upcoming Tests</h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                        {scheduledTests.map((test, i) => (
                            <GlassmorphicCard key={i} className="p-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                        <Calendar className="h-5 w-5 text-blue-500" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-theme-primary text-sm">{test.name}</p>
                                        <p className="text-xs text-theme-secondary">{test.date} • {test.time}</p>
                                    </div>
                                </div>
                                <button className="p-2 text-theme-secondary hover:text-primary-blue transition-colors">
                                    <Bell className="h-5 w-5" />
                                </button>
                            </GlassmorphicCard>
                        ))}
                    </div>
                </div>
            )}

            {/* Completed Tests */}
            {(activeFilter === 'all' || activeFilter === 'completed') && (
                <div>
                    <h3 className="text-base md:text-lg font-bold text-theme-primary mb-3">Completed Tests</h3>
                    <div className="space-y-3">
                        {pastTests.map((test) => (
                            <GlassmorphicCard key={test.num} className="p-3 md:p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                <div className="flex items-center gap-3 md:gap-4">
                                    <div className="p-2 md:p-2.5 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                        <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-500" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-theme-primary text-sm md:text-base">Weekly Mock Test {test.num}</h4>
                                        <p className="text-xs text-theme-secondary">{test.date}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 md:gap-6">
                                    <div className="text-right hidden sm:block">
                                        <p className="text-xs text-theme-secondary">Score</p>
                                        <p className="font-bold text-theme-primary">{test.score}/720</p>
                                    </div>
                                    <p className="font-bold text-theme-primary sm:hidden text-sm">{test.score}</p>
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => handleViewReport(test.score, 720)}
                                        className="px-3 md:px-4 py-2 text-xs md:text-sm font-medium border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                    >
                                        View
                                    </motion.button>
                                </div>
                            </GlassmorphicCard>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}



// ============================================
// MAIN DASHBOARD PAGE
// ============================================
export default function DashboardPage() {
    const [activeTab, setActiveTab] = useState('dashboard')
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

    const handleNavigation = (tab: string) => {
        setActiveTab(tab)
        setIsMobileMenuOpen(false)
    }

    return (
        <div className="h-screen w-full flex bg-slate-50 dark:bg-slate-950 overflow-hidden">
            {/* Desktop Sidebar */}
            <div className="hidden lg:block pl-4 py-4 h-full">
                <StudentSidebar activeTab={activeTab} setActiveTab={setActiveTab} />
            </div>

            {/* Main Content Area */}
            <main className="flex-1 flex flex-col h-full overflow-hidden relative">
                {/* Header with Mobile Menu Button */}
                <div className="px-4 pt-4 lg:pt-6 lg:px-6 shrink-0">
                    <div className="flex items-center gap-4">
                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setIsMobileMenuOpen(true)}
                            className="lg:hidden p-2 rounded-lg bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors"
                        >
                            <Menu className="h-5 w-5 text-theme-primary" />
                        </button>

                        <div className="flex-1">
                            <StudentHeader toggleSidebar={() => { }} showMenuButton={false} />
                        </div>
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto px-4 pb-24 lg:px-6 lg:pb-6 custom-scrollbar">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                        className="max-w-6xl mx-auto"
                    >
                        {activeTab === 'dashboard' && <DashboardOverview setActiveTab={setActiveTab} />}
                        {activeTab === 'daily-plan' && <DailyPlanView />}
                        {activeTab === 'discipline' && <DisciplineTrackerView />}
                        {activeTab === 'tests' && <TestsView />}
                        {activeTab === 'test-history' && <TestHistoryView />}
                        {activeTab === 'study-material' && <StudyMaterialView />}
                        {activeTab === 'question-bank' && <StudentQuestionBankView />}
                        {activeTab === 'performance' && <PerformanceAnalyticsView />}
                        {activeTab === 'chat' && <ChatView />}
                        {activeTab === 'announcements' && <AnnouncementsView />}
                        {activeTab === 'leaderboard' && <LeaderboardView />}
                        {activeTab === 'profile' && <ProfileView />}
                        {activeTab === 'payments' && <PaymentsView />}
                        {activeTab === 'support' && <SupportView />}

                    </motion.div>
                </div>
            </main>

            {/* Mobile Bottom Navigation */}
            <StudentBottomNav activeTab={activeTab} setActiveTab={setActiveTab} />

            {/* Mobile Menu */}
            <StudentMobileMenu
                isOpen={isMobileMenuOpen}
                onClose={() => setIsMobileMenuOpen(false)}
                activeTab={activeTab}
                onNavigate={handleNavigation}
            />
        </div>
    )
}
